import { showsActivityPrice } from "@majourneys/shared";
// The hub grid, growing as the guest scrolls.
//
// The next page loads when the end of the list comes into view and is
// appended under the cards already on screen. The loader is still a real link
// to ?page=N+1, so a crawler walks every page the way it did through the
// numbered pagination; a guest only sees it as a spinner, or as a retry
// button when a page failed to load.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDisplayPriceParts } from "../../../lib/currency";
import { activityHrefString } from "../lib/activity-href";
import { StaysHubMapLazy, type StaysMapPin } from "../../stays/components/stays-hub-map-lazy";
import { MapViewStyles } from "../../stays/components/map-view-styles";
import type { MapEntry } from "../../stays/components/stays-hub-grid";
import { glideCards, measureCards } from "../../stays/lib/motion";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useLoadOnScroll } from "../../../shared/hooks/use-load-on-scroll";
import { activitiesApi } from "../api/activities.api";
import type { ActivityFilters } from "../constants/query-keys";
import { ActivityGrid } from "./activity-grid";
import { HubPhoneMap, type HubPhoneMapProps } from "./hub-phone-map";
import { HubMapRows, type MapRowItem } from "./hub-map-rows";
import type { PhoneMapStay } from "../../stays/components/stays-phone-map";
import { useIsDesk } from "../../../shared/hooks/use-is-desk";

const NAVY = "#110E39";
const META = "#5a5d77";

type Item = Parameters<typeof ActivityGrid>[0]["items"][number];

/**
 * What "Show more" had loaded, per list, for the life of the tab. Opening an
 * activity and coming Back remounts this grid; without this the guest lands
 * on the first 12 again, far above the card they had scrolled to.
 */
const loadedByList = new Map<string, { extra: Item[]; loaded: number }>();

/** A bookable row's category, in the Discover map's colours and badges. */
const PIN_SEGMENT: Record<string, string> = {
  restaurants: "restaurants",
  spas: "spas",
  pools: "beach-clubs",
  "beach-clubs": "beach-clubs",
  nightclubs: "nightlife",
  golf: "golf",
  camp: "stays",
};

export function HubShowMoreGrid({
  items,
  page,
  totalPages,
  total,
  pageSize,
  filters,
  mapOn = false,
  lang = "en",
  phoneMap,
  markUnbookable,
  compactRows = false,
  linkSearch,
}: {
  /** Badge non-bookable rows "Not bookable" (Restaurants and Spas hubs). */
  markUnbookable?: boolean;
  /**
   * #67: on a desk with the map on, compact numbered rows beside numbered
   * pins instead of the two-column cards. Phones keep their map layer.
   */
  compactRows?: boolean;
  /** The booking bar's day, time and guests, carried into bookable links. */
  linkSearch?: Record<string, string | number>;
  /** List on the left, map on the right (a desk); a full-screen layer (a phone). */
  mapOn?: boolean;
  /** The phone's map layer: closing it, and the hub controls in its sheet. */
  phoneMap?: Pick<HubPhoneMapProps, "onClose" | "categorySlug" | "citySlug" | "collections">;
  lang?: string;
  items: Item[];
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  filters: ActivityFilters;
}) {
  const { t } = useTranslation();
  // A new city, type, price or sort is a different list.
  const key = useMemo(() => {
    const { page: _p, ...rest } = filters;
    return JSON.stringify(rest) + `|${page}`;
  }, [filters, page]);
  const [extra, setExtra] = useState<Item[]>(() => loadedByList.get(key)?.extra ?? []);
  const [loaded, setLoaded] = useState(() => loadedByList.get(key)?.loaded ?? page);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [listKey, setListKey] = useState(key);
  // The list a request was made for; a reply for an older list is dropped.
  const liveKey = useRef(key);
  liveKey.current = key;
  if (listKey !== key) {
    // Switched list while mounted: pick up what that list had, or start over.
    setListKey(key);
    setExtra(loadedByList.get(key)?.extra ?? []);
    setLoaded(loadedByList.get(key)?.loaded ?? page);
  }
  useEffect(() => {
    if (extra.length) loadedByList.set(key, { extra, loaded });
  }, [key, extra, loaded]);

  const all = extra.length ? [...items, ...extra] : items;

  /* Map view: a pin for every card loaded so far, labelled with the price the
     card shows. "Show more" adds pins as it adds cards. */
  const displayMad = useDisplayPriceParts();
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const isDeskNow = useIsDesk();
  const numbered = compactRows && isDeskNow;
  const entries = useMemo(() => {
    const out = new Map<string, MapEntry>();
    if (!mapOn) return out;
    (all as (Item & { latitude?: number | null; longitude?: number | null })[]).forEach((a, index) => {
      const has = typeof a.latitude === "number" && typeof a.longitude === "number";
      // Same rule as the card: no amount on a restaurant or an editorial place.
      const p = showsActivityPrice({ price: a.price, mode: a.mode, categorySlug: a.category?.slug, segment: a.segment })
        ? displayMad(a.price)
        : null;
      const href = activityHrefString({ lang, citySlug: a.city.slug, categorySlug: a.category?.slug, segment: a.segment, slug: a.slug });
      out.set(a.id, {
        // The web Discover pin: the photo, the category's ring and badge, and
        // what the preview card needs (owner, 2026-09-30).
        pin: has
          ? {
              id: a.id,
              lat: a.latitude as number,
              lng: a.longitude as number,
              // Numbered pins match the numbered rows (#67).
              label: numbered ? String(index + 1) : p ? `${p.value} ${p.currency}` : "",
              title: a.title,
              image: a.images[0],
              segment: a.segment ?? PIN_SEGMENT[a.category?.slug ?? ""],
              mode: a.segment ? "explore" : "bookable",
              meta: [a.category?.name, a.city.name].filter(Boolean).join(" · "),
              href,
            }
          : null,
        name: a.title,
        image: a.images[0],
        href,
      });
    });
    return out;
  }, [mapOn, all, displayMad, lang, numbered]);
  const pins = useMemo(
    () => [...entries.values()].flatMap((e): StaysMapPin[] => (e.pin ? [e.pin] : [])),
    [entries],
  );
  const activeId = hoverId ?? pickedId;
  /* Phones get the full-screen map layer, as on Stays; a desk keeps the
     side-by-side. The list stays put under the layer. */
  const isDesk = isDeskNow;
  const deskMap = mapOn && isDesk;
  const phoneItems = useMemo(() => {
    const out = new Map<string, PhoneMapStay>();
    if (!mapOn || isDesk) return out;
    for (const a of all as (Item & { latitude?: number | null; longitude?: number | null })[]) {
      const e = entries.get(a.id);
      if (!e?.pin) continue;
      out.set(a.id, {
        id: a.id,
        name: a.title,
        image: a.images[0],
        href: e.href,
        typeLabel: a.category?.name,
        city: a.city.name,
        // The picked card's second line: where it is.
        nearby: a.city.name,
        save: {
          kind: "activity",
          item: { slug: a.slug, title: a.title, city: a.city.name, price: a.price, currency: "MAD", image: a.images[0], category: a.category?.name },
        },
        price: e.pin.label || undefined,
        lat: e.pin.lat,
        lng: e.pin.lng,
      });
    }
    return out;
  }, [mapOn, isDesk, all, entries]);
  useEffect(() => {
    if (!mapOn) setPickedId(null);
  }, [mapOn]);

  /** A tapped pin brings its card on screen (a desk; a phone shows the small card). */
  function pickFromMap(id: string | null) {
    setPickedId(id);
    if (!id || typeof window === "undefined" || !window.matchMedia("(min-width: 1024px)").matches) return;
    document.getElementById(`stay-hub-card-${id}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  /* List to map and back: the cards glide to their new places, as on Stays. */
  const listRef = useRef<HTMLDivElement>(null);
  const flipBefore = useRef<Map<string, DOMRect> | null>(null);
  const flipMapOn = useRef(deskMap);
  if (flipMapOn.current !== deskMap) {
    flipMapOn.current = deskMap;
    flipBefore.current = typeof window !== "undefined" ? measureCards(listRef.current) : null;
  }
  useLayoutEffect(() => {
    if (!flipBefore.current) return;
    glideCards(listRef.current, flipBefore.current);
    flipBefore.current = null;
  }, [deskMap]);
  const seen = Math.min(total, (page - 1) * pageSize + all.length);
  const next = loaded + 1;
  const hasMore = next <= totalPages;

  async function loadMore() {
    if (busy || !hasMore) return;
    const askedFor = key;
    setBusy(true);
    setFailed(false);
    try {
      const res = (await activitiesApi.list({ ...filters, page: String(next) })) as { data: Item[] };
      if (liveKey.current !== askedFor) return;
      setExtra((prev) => [...prev, ...res.data]);
      setLoaded(next);
    } catch {
      if (liveKey.current === askedFor) setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  const moreRef = useRef<HTMLAnchorElement>(null);
  // A failed page waits for the retry button rather than looping on errors.
  useLoadOnScroll(moreRef, { enabled: hasMore && !failed, step: all.length, onLoad: () => void loadMore() });

  return (
    <div
      ref={listRef}
      className={deskMap ? "mj-stays-split lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-6" : undefined}
    >
    <div>
      {deskMap && compactRows ? (
        <HubMapRows
          items={all as unknown as MapRowItem[]}
          activeId={activeId}
          onHover={setHoverId}
          lang={lang}
          linkSearch={linkSearch}
        />
      ) : (
        <ActivityGrid
          items={all}
          page={page}
          totalPages={totalPages}
          onPageChange={() => {}}
          hidePagination
          mapMode={deskMap ? { activeId, onHover: setHoverId } : undefined}
          markUnbookable={markUnbookable}
          linkSearch={linkSearch}
          preferCuisine={filters.cuisine ?? null}
          oneCity={!!filters.city}
        />
      )}

      {total > 0 ? (
        <div className="mt-12 flex flex-col items-center gap-3">
          <p className="m-0 text-[14px]" style={{ color: META }}>
            {page > 1
              ? // Arrived mid-list (a search result or a shared ?page=2 link):
                // say which slice this is rather than claim 24 were seen.
                t("explore.showing_range", {
                  from: (page - 1) * pageSize + 1,
                  to: seen,
                  total,
                  defaultValue: `Showing ${(page - 1) * pageSize + 1}–${seen} of ${total}`,
                })
              : t("explore.seen_of", { seen, total, defaultValue: `You've seen ${seen} of ${total}` })}
          </p>
          <div className="h-[3px] w-[180px] overflow-hidden rounded-full" style={{ background: "#E3E5EE" }}>
            <div
              className="h-full w-full origin-left rounded-full"
              style={{
                background: NAVY,
                transform: `scaleX(${total ? seen / total : 0})`,
                transition: "transform 420ms cubic-bezier(.2,.8,.2,1)",
              }}
            />
          </div>
          {hasMore ? (
            <Link
              ref={moreRef}
              to="."
              search={((prev: Record<string, unknown>) => ({ ...prev, page: next })) as never}
              resetScroll={false}
              onClick={(e) => {
                // Cmd/Ctrl/Shift-click still opens page N+1 as a link.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                void loadMore();
              }}
              aria-busy={busy}
              aria-label={t("explore.show_more", { defaultValue: "Show more" })}
              className={
                failed
                  ? "mt-3 inline-flex h-12 min-w-[150px] items-center justify-center rounded-full bg-white px-7 text-[15px] font-semibold no-underline transition-[transform,box-shadow] duration-200 active:scale-[0.97]"
                  : "mt-3 inline-flex h-12 w-12 items-center justify-center no-underline"
              }
              style={failed ? { color: NAVY, boxShadow: "0 1px 2px rgba(17,14,57,0.06), 0 6px 18px -6px rgba(17,14,57,0.18)" } : undefined}
            >
              {failed ? (
                t("explore.show_more", { defaultValue: "Show more" })
              ) : busy ? (
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#110E39] border-t-transparent" aria-hidden />
              ) : null}
            </Link>
          ) : null}
          {failed ? (
            <p role="alert" className="m-0 text-[13.5px]" style={{ color: "#b42318" }}>
              {t("explore.load_failed", { defaultValue: "That didn't load. Try again." })}
            </p>
          ) : null}
          {page > 1 ? (
            <Link
              to="."
              search={((prev: Record<string, unknown>) => {
                const { page: _p, ...rest } = prev;
                return rest;
              }) as never}
              className="mt-1 inline-flex min-h-8 items-center text-[14px] font-semibold underline underline-offset-4"
              style={{ color: NAVY }}
            >
              {t("explore.from_start", { defaultValue: "See from the start" })}
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
    {mapOn && !isDesk && phoneMap ? (
      <HubPhoneMap
        {...phoneMap}
        lang={lang}
        pins={pins}
        items={phoneItems}
        fitKey={key.split("|")[0]!}
        total={total}
        filters={filters}
      />
    ) : null}
    {deskMap ? (
      <div className="lg:sticky lg:self-start mj-stays-map-pin">
        <MapViewStyles />
        <StaysHubMapLazy
          pins={pins}
          fitKey={key.split("|")[0]}
          emptyIcon="ri-map-pin-2-line"
          variant={compactRows ? "pill" : "photo"}
          activeId={activeId}
          onHover={setHoverId}
          onSelect={pickFromMap}
          previewId={pickedId}
          onPreviewClose={() => setPickedId(null)}
          className="mj-stays-map mj-map-enter h-[calc(100dvh-300px)] min-h-[420px]"
        />
      </div>
    ) : null}
    </div>
  );
}
